import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import useAuth from "../hooks/useAuth";
import * as transport from "../utils/downloadTransport";

const {
  DOWNLOAD_STATUS,
  ACTIVE_DOWNLOAD_STATUSES,
  DOWNLOAD_CONCURRENCY,
  DownloadRequestError,
} = transport;

export const DOWNLOAD_QUEUE_STORAGE_KEY = "shotkut_download_queue_v1";
const MAX_STORED_ITEMS = 25;
const PROGRESS_THROTTLE_MS = 150;

const DownloadQueueContext = createContext(null);

const isActive = (item) => ACTIVE_DOWNLOAD_STATUSES.has(item.status);

let idCounter = 0;
const createItemId = () => {
  idCounter += 1;
  return `dl-${Date.now().toString(36)}-${idCounter}`;
};

const PERSISTED_FIELDS = [
  "id", "videoId", "title", "thumbnail", "requestedQuality", "quality",
  "fallbackReason", "status", "mode", "error", "errorCode", "retryable",
  "bytesReceived", "totalBytes", "createdAt", "startedAt", "finishedAt",
];

const toStoredItem = (item) =>
  PERSISTED_FIELDS.reduce((out, key) => {
    if (item[key] !== undefined) out[key] = item[key];
    return out;
  }, {});

/** A reload kills any in-flight transfer; never restore it as still running. */
export function restoreQueue(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item.id === "string" && item.videoId)
    .slice(-MAX_STORED_ITEMS)
    .map((item) =>
      ACTIVE_DOWNLOAD_STATUSES.has(item.status)
        ? {
            ...item,
            status: DOWNLOAD_STATUS.FAILED,
            error: "Interrupted — the page was reloaded before this download finished.",
            errorCode: "INTERRUPTED",
            retryable: true,
            finishedAt: item.finishedAt || Date.now(),
          }
        : item,
    );
}

const loadInitialQueue = () => {
  try {
    const stored = window.localStorage.getItem(DOWNLOAD_QUEUE_STORAGE_KEY);
    return stored ? restoreQueue(JSON.parse(stored)) : [];
  } catch {
    return [];
  }
};

function queueReducer(items, action) {
  switch (action.type) {
    case "ADD":
      return [
        ...items.filter((i) => i.videoId !== action.item.videoId || isActive(i)),
        action.item,
      ];
    case "UPDATE":
      return items.map((i) => (i.id === action.id ? { ...i, ...action.patch } : i));
    case "REQUEUE": {
      const target = items.find((i) => i.id === action.id);
      if (!target) return items;
      return [
        ...items.filter((i) => i.id !== action.id),
        {
          ...target,
          status: DOWNLOAD_STATUS.QUEUED,
          error: null,
          errorCode: null,
          bytesReceived: 0,
          totalBytes: null,
          startedAt: null,
          finishedAt: null,
          createdAt: Date.now(),
        },
      ];
    }
    case "REMOVE":
      return items.filter((i) => i.id !== action.id || isActive(i));
    case "CLEAR_FINISHED":
      return items.filter(isActive);
    default:
      return items;
  }
}

export const DownloadQueueProvider = ({
  children,
  concurrency = DOWNLOAD_CONCURRENCY,
  inPageLimitBytes = transport.IN_PAGE_DOWNLOAD_LIMIT_BYTES,
}) => {
  const { auth, setAuth } = useAuth() || {};
  const [items, dispatch] = useReducer(queueReducer, undefined, loadInitialQueue);
  const [expanded, setExpanded] = useState(true);

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const authRef = useRef(auth);
  authRef.current = auth;
  const setAuthRef = useRef(setAuth);
  setAuthRef.current = setAuth;

  const runningRef = useRef(new Set());
  const controllersRef = useRef(new Map());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const controllers = controllersRef.current;
    return () => {
      mountedRef.current = false;
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        DOWNLOAD_QUEUE_STORAGE_KEY,
        JSON.stringify(items.slice(-MAX_STORED_ITEMS).map(toStoredItem)),
      );
    } catch {
      // Storage full or disabled; the queue still works for this page view.
    }
  }, [items]);

  const prepareWithAuth = useCallback(async (videoId, signal) => {
    const token = authRef.current?.RawToken;
    if (!token) {
      throw new DownloadRequestError("Please sign in to download.", {
        code: "AUTH_REQUIRED",
        authExpired: true,
      });
    }
    try {
      return await transport.prepareDownload({ videoId, token, signal });
    } catch (err) {
      if (!err?.authExpired || signal.aborted) throw err;
      let freshToken;
      try {
        freshToken = await transport.refreshAccessToken();
      } catch {
        throw err;
      }
      if (signal.aborted) throw transport.cancelledError();
      try {
        setAuthRef.current?.({ ...jwtDecode(freshToken), RawToken: freshToken });
      } catch {
        // Token still usable for this request even if it can't be decoded.
      }
      return transport.prepareDownload({ videoId, token: freshToken, signal });
    }
  }, []);

  const runJob = useCallback(
    async (item) => {
      runningRef.current.add(item.id);
      const controller = new AbortController();
      controllersRef.current.set(item.id, controller);
      const { signal } = controller;
      const update = (patch) => {
        if (mountedRef.current) dispatch({ type: "UPDATE", id: item.id, patch });
      };

      update({ status: DOWNLOAD_STATUS.PREPARING, startedAt: Date.now() });

      try {
        const prepared = await prepareWithAuth(item.videoId, signal);
        update({
          quality: prepared.quality,
          fallbackReason: prepared.fallbackReason || null,
          title: item.title || prepared.title || item.title,
          totalBytes: prepared.sizeBytes ?? null,
        });

        const url = transport.buildStreamUrl(item.videoId, prepared.ticket);

        if (transport.canStreamInPage(prepared.sizeBytes, inPageLimitBytes)) {
          update({ status: DOWNLOAD_STATUS.DOWNLOADING, mode: "stream" });
          let lastUpdate = 0;
          const result = await transport.streamDownload({
            url,
            signal,
            expectedBytes: prepared.sizeBytes,
            onProgress: ({ received, total }) => {
              const now = Date.now();
              if (now - lastUpdate < PROGRESS_THROTTLE_MS && received !== total) return;
              lastUpdate = now;
              update({ bytesReceived: received, totalBytes: total ?? null });
            },
          });
          if (signal.aborted) throw transport.cancelledError();
          transport.saveBlob(result.blob, result.fileName || prepared.fileName);
          update({
            status: DOWNLOAD_STATUS.COMPLETED,
            bytesReceived: result.received,
            totalBytes: result.total,
            quality: result.quality || prepared.quality,
            fallbackReason: result.fallbackReason || prepared.fallbackReason || null,
            finishedAt: Date.now(),
          });
        } else {
          if (signal.aborted) throw transport.cancelledError();
          transport.triggerBrowserDownload(url, prepared.fileName);
          update({
            status: DOWNLOAD_STATUS.COMPLETED,
            mode: "browser",
            finishedAt: Date.now(),
          });
        }
      } catch (err) {
        if (signal.aborted || err?.code === "CANCELLED") {
          update({ status: DOWNLOAD_STATUS.CANCELLED, finishedAt: Date.now() });
        } else {
          update({
            status: DOWNLOAD_STATUS.FAILED,
            error: err?.message || "Download failed. Please try again.",
            errorCode: err?.code || "DOWNLOAD_FAILED",
            retryable: err?.retryable !== false,
            finishedAt: Date.now(),
          });
        }
      } finally {
        runningRef.current.delete(item.id);
        controllersRef.current.delete(item.id);
      }
    },
    [prepareWithAuth, inPageLimitBytes],
  );

  useEffect(() => {
    const free = concurrency - runningRef.current.size;
    if (free <= 0) return;
    items
      .filter((i) => i.status === DOWNLOAD_STATUS.QUEUED && !runningRef.current.has(i.id))
      .slice(0, free)
      .forEach((item) => {
        runJob(item);
      });
  }, [items, concurrency, runJob]);

  const enqueueDownload = useCallback(({ videoId, title, thumbnail } = {}) => {
    if (!videoId) return { added: false, reason: "invalid" };
    const key = String(videoId);
    const existing = itemsRef.current.find((i) => i.videoId === key && isActive(i));
    if (existing) return { added: false, reason: "duplicate", item: existing };

    const item = {
      id: createItemId(),
      videoId: key,
      title: title || null,
      thumbnail: thumbnail || null,
      requestedQuality: "4K",
      quality: null,
      fallbackReason: null,
      status: DOWNLOAD_STATUS.QUEUED,
      mode: null,
      error: null,
      errorCode: null,
      retryable: true,
      bytesReceived: 0,
      totalBytes: null,
      createdAt: Date.now(),
      startedAt: null,
      finishedAt: null,
    };
    // Updated synchronously so a second click in the same tick sees this item.
    itemsRef.current = queueReducer(itemsRef.current, { type: "ADD", item });
    dispatch({ type: "ADD", item });
    setExpanded(true);
    return { added: true, item };
  }, []);

  const cancelDownload = useCallback((id) => {
    const item = itemsRef.current.find((i) => i.id === id);
    if (!item || !isActive(item)) return;
    const controller = controllersRef.current.get(id);
    if (controller) {
      controller.abort();
    } else {
      dispatch({
        type: "UPDATE",
        id,
        patch: { status: DOWNLOAD_STATUS.CANCELLED, finishedAt: Date.now() },
      });
    }
  }, []);

  const retryDownload = useCallback((id) => {
    const item = itemsRef.current.find((i) => i.id === id);
    if (!item || isActive(item)) return;
    const duplicate = itemsRef.current.some(
      (i) => i.id !== id && i.videoId === item.videoId && isActive(i),
    );
    if (duplicate) return;
    itemsRef.current = queueReducer(itemsRef.current, { type: "REQUEUE", id });
    dispatch({ type: "REQUEUE", id });
    setExpanded(true);
  }, []);

  const dismissDownload = useCallback((id) => dispatch({ type: "REMOVE", id }), []);
  const clearFinished = useCallback(() => dispatch({ type: "CLEAR_FINISHED" }), []);

  const isDownloadActive = useCallback(
    (videoId) => items.some((i) => i.videoId === String(videoId) && isActive(i)),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      expanded,
      setExpanded,
      enqueueDownload,
      cancelDownload,
      retryDownload,
      dismissDownload,
      clearFinished,
      isDownloadActive,
    }),
    [
      items,
      expanded,
      enqueueDownload,
      cancelDownload,
      retryDownload,
      dismissDownload,
      clearFinished,
      isDownloadActive,
    ],
  );

  return (
    <DownloadQueueContext.Provider value={value}>
      {children}
    </DownloadQueueContext.Provider>
  );
};

export const useDownloadQueue = () => {
  const context = useContext(DownloadQueueContext);
  if (!context) {
    throw new Error("useDownloadQueue must be used inside DownloadQueueProvider");
  }
  return context;
};
